import { makeStyles } from '@material-ui/core';
import { hasPermission } from "common/access-control";
import { PropertySearchDTO } from 'common/dto';
import usePageTitle, { useForm } from 'common/hooks';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { getInitialPropertyMatchingSearchFormValues, IRootState, PASDispatch } from 'reducers';
import PropertiesMatchingSearch from './components/PropertiesMatchingSearch';
import PropertiesMatchingTable from './components/PropertiesMatchingTable';
import PropertiesMatchingToolbar from './components/PropertiesMatchingToolbar';




export const PS_DEFAULT_SIZE = 10;

export interface PropertiesMatchingSearchFormValues extends PropertySearchDTO {
  // Toggling
  isRent: boolean;
  isGross: boolean;
  disabledCriteriaList: { [k: string]: boolean };
  isValidRange: boolean;
}

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2)
  },
  content: {
    marginTop: theme.spacing(2)
  },
  search: {
    marginBottom: theme.spacing(2)
  }
}));

export let initialSortOrder: { [k: string]: 'desc' | 'asc' } = {
  // price: 'desc',
  dateModified: 'desc',
};

export let initialPage = 0;
let currentPropertyRow = 0;
const PropertiesMatchingList = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch() as PASDispatch;
  const propertyMatching = useSelector((state: IRootState) => state.propertyMatching);
  const clientPreference = useSelector((state: IRootState) => state.propertyMatching?.clientPreference);
  let lastSearchForm = useSelector((state: IRootState) => state.property.lastSearchFormValuesForPropertiesMatching ?? null);
  const { currentMatchingList: properties, totalMatchingPages, totalMatchingElements } = useSelector((state: IRootState) => state.property);
  const isRent = useSelector((state: IRootState) => state.property.lastSearchFormValuesForPropertiesMatching?.isRent ?? false);
  const { langPropertyStock, locale, rowsCountPrefOptions } = useSelector((state: IRootState) => state.locale);

  const { privileges } = useSelector((state: IRootState) => state.login);
  const canReadUnownedProperty = hasPermission(privileges, 'READ', 'UNOWNED_PROPERTY_STOCK');
  const canReadTeamProperty = hasPermission(privileges, 'READ', 'TEAM_PROPERTY_STOCK');
  // const rowsCountPreference = useSelector((state: IRootState) => state.login.rowsCountPreference) ?? EMPTY_STRING;

  // When matching, show ONLY those passing approval stage 1
  // const showApprovedOnly = !!propertyMatching;

  usePageTitle(langPropertyStock.titlePropertyMatching);
  
  const [page, setPage] = useState(initialPage);
  initialPage = page;

  const limitPref = useSelector((state: IRootState) => state.login.rowsCountPreference?.PROPERTY_STOCK) ?? PS_DEFAULT_SIZE;
  // const [ limitPref, setLimitPref ] = useState(limit);

  const [currentRowsPerPage, setCurrentRowsPerPage] = useState(currentPropertyRow === 0 ? limitPref : currentPropertyRow);
  const [sortOrders, setSortOrders] = useState<{ [k: string]: 'desc' | 'asc' }>(initialSortOrder);

  // Work-around solution (same as page)
  initialSortOrder = sortOrders;

  currentPropertyRow = currentRowsPerPage;
  // 

  const searchForm = useForm<PropertiesMatchingSearchFormValues>(
    getInitialPropertyMatchingSearchFormValues(clientPreference, locale)
  );


  // Search Request Handling

  const handleSearch = () => {
    setPage(0);
    dispatch({
      type: 'PropertyList.FetchRequested', payload: {
        ...searchForm.values as any,
        page: 0,
        size: currentRowsPerPage,
        sort: Object.keys(sortOrders)[0] === 'price' && searchForm.values.isRent ? { ['rent']: sortOrders[Object.keys(sortOrders)[0]] } :
          Object.keys(sortOrders)[0] === 'price' && !searchForm.values.isRent ? { ['price']: sortOrders[Object.keys(sortOrders)[0]] } :
            Object.keys(sortOrders)[0] === 'pricePerNet' && searchForm.values.isRent ? { ['rentPerNet']: sortOrders[Object.keys(sortOrders)[0]] } :
              Object.keys(sortOrders)[0] === 'pricePerNet' && !searchForm.values.isRent ? { ['pricePerNet']: sortOrders[Object.keys(sortOrders)[0]] } :
                Object.keys(sortOrders)[0] === 'pricePerGross' && searchForm.values.isRent ? { ['rentPerGross']: sortOrders[Object.keys(sortOrders)[0]] } :
                  Object.keys(sortOrders)[0] === 'pricePerGross' && !searchForm.values.isRent ? { ['pricePerGross']: sortOrders[Object.keys(sortOrders)[0]] } :
                    sortOrders,
      }
    });
  };

  const handleRowsCount = (count: number) => {
    dispatch({ type: 'Login.RowCountPrefUpdate', payload: { PROPERTY_STOCK: count } });
  }

  const handleSort = (key: string | null) => {
    if (key === null) {
      setSortOrders({});
      dispatch({
        type: 'PropertyList.FetchRequested', payload: {
          // lastSearchFormValues: searchForm.values,
          ...searchForm.values as any,
          page, size: currentRowsPerPage, sort: {},
        }
      });
      return;
    }
    const newSortOrders = { [key]: sortOrders[key] === 'asc' ? 'desc' : 'asc' };
    setSortOrders(newSortOrders as any);
    const searchFormWithSort = lastSearchForm == null ? searchForm?.values : lastSearchForm
    dispatch({
      type: 'PropertyList.FetchRequested', payload: {
        // lastSearchFormValues: searchForm.values,
        // ...searchForm.values as any,
        ...searchFormWithSort as any,
        page, size: currentRowsPerPage,
        sort:
          key === 'price' && isRent ? { ['rent']: sortOrders[key] === 'asc' ? 'desc' : 'asc' } :
            key === 'pricePerNet' && isRent ? { ['rentPerNet']: sortOrders[key] === 'asc' ? 'desc' : 'asc' } :
              key === 'pricePerGross' && isRent ? { ['rentPerGross']: sortOrders[key] === 'asc' ? 'desc' : 'asc' } :
                newSortOrders,
      }
    });
  }

  const handleUpdateStockCart = (id: string[]) => {
    dispatch({
      type: 'PropertyMatching.AddStockCartRequested', payload: {
        cid: propertyMatching?.cid ?? '', pid: id ?? []
      }
    })
    searchForm.setValues(getInitialPropertyMatchingSearchFormValues(undefined, locale));
    dispatch({
      type: 'PropertyList.FetchRequested',
      payload: {
        ...getInitialPropertyMatchingSearchFormValues({}, locale) as any,
        page: initialPage, size: limitPref, sort: initialSortOrder,
      }
    });
    dispatch({ type: 'PropertyList.ForceCancelPropertyMatching', payload: false })
  }

  const handleCancelPropertyMatching = () => {
    dispatch({ type: 'PropertyMatching.StopMatching' });
    history.push(`/client-detail/${propertyMatching?.cid ?? 0}?tab=${encodeURIComponent('wishlist')}`);
    searchForm.setValues(getInitialPropertyMatchingSearchFormValues(undefined, locale));
    dispatch({
      type: 'PropertyList.FetchRequested',
      payload: {
        ...getInitialPropertyMatchingSearchFormValues({}, locale) as any,
        page: initialPage, size: limitPref, sort: initialSortOrder,
      }
    });
  }

  // Initial Fetch
  useEffect(() => {
    if (propertyMatching?.cid == undefined || propertyMatching?.cid == '') {
      history.replace('/client-detail')
    } else {
      dispatch({
        type: 'PropertyList.FetchRequested', payload: {
          // lastSearchFormValues: searchForm.values,
          ...searchForm.values as any,
          page, size: currentRowsPerPage, sort: sortOrders,
          // isCurrentUser: true,
        }
      });
    }
  }, [currentRowsPerPage]);

  useEffect(() => {
    dispatch({ type: 'Layout.MaskDismissRequested' });
  }, [properties]);

  return (
    <div className={classes.root}>
      <PropertiesMatchingToolbar />
      <div className={classes.content}>
        <PropertiesMatchingSearch form={searchForm} onSearch={handleSearch} wishlistVariant={!!propertyMatching} canReadUnownedProperty={canReadUnownedProperty} canReadTeamProperty={canReadTeamProperty} className={classes.search}></PropertiesMatchingSearch>
        <PropertiesMatchingTable
          page={page}
          totalPages={totalMatchingPages}
          totalElements={totalMatchingElements}
          limit={currentRowsPerPage}
          setPage={(page) => {
            setPage(page);
            dispatch({
              type: 'PropertyList.FetchRequested', payload: {
                ...searchForm.values as any,
                page, size: currentRowsPerPage, sort: sortOrders,
              }
            });
          }}
          rowsCountOptions={Object.keys(rowsCountPrefOptions).map(Number)}
          handleRowsCount={(ev) => { setCurrentRowsPerPage(ev); handleRowsCount(ev) }}
          properties={properties}
          sortOrders={sortOrders}
          handleSort={handleSort}
          matchingClient={propertyMatching}
          handleUpdateStockCart={handleUpdateStockCart}
          onCancelPropertyMatching={handleCancelPropertyMatching}
          fileForm={null}
        />
      </div>
    </div>
  );
};

export default PropertiesMatchingList;